import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Checkbox,
  EditableValueCell,
  EntitySelectorContent,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorTrigger,
  InlineValueTrigger,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  Text,
  type EditableValueCellAction,
  type EditableValueCellApi,
  type EditableValueCellEditor,
  type Omittable,
} from "@salestrak/ui";
import {
  getDefaultValueByType,
  validateCustomFieldValue,
} from "@salestrak/custom-fields";
import { UserSelector } from "#app/components/entity-selectors";
import { USER_POOL_ID } from "#app/components/entity-selectors/hooks/use-user-pool";
import {
  FIELD_MEDIA_TILE_SIZE,
  ResolvedMediaTile,
} from "#app/components/media/media-tiles";
import { MediaLightbox } from "#app/components/media/media-lightbox";
import {
  CustomFieldRenderer,
  OptionValueChips,
  getCustomFieldDisplayValue,
  isCustomFieldValueEmpty,
} from "./custom-field-renderers";
import {
  OptionSelector,
  OptionSelectorTrigger,
  type OnCreateOption,
} from "./option-picker";
import type { OptionActions } from "#app/lib/hooks/use-custom-field-mutations";
import { CustomFieldMemberValue } from "./custom-field-member-value";
import { FieldDatePicker } from "./field-date-picker";
import {
  isMultipleField,
  NONE_OPTION_LABEL,
} from "#app/lib/custom-field-utils";
import { RichTextEditor } from "#app/components/editor/rich-text-editor";
import { normalizeRichTextValue } from "#app/lib/utils/rich-text-value";
import {
  blurActiveElement,
  buildCopyValueAction,
  getFieldTypeDescriptor,
  getProtocolHref,
  getValueActionText,
} from "#app/components/fields/field-type-descriptors";
import type { CustomFieldDef } from "./types";
import { InvalidPhoneMarker, isFlaggedPhoneValue } from "./invalid-phone-marker";
import { UrlFavicon } from "#app/components/fields/url-favicon";
import { useOpenAddressOnMap } from "#app/lib/hooks/use-open-address-on-map";

/**
 * The custom-field VALUE cell: composes the library's `EditableValueCell`
 * (which owns activation, touch reveal, outside-click dismissal, validation
 * gating, value actions and reset) with this app's per-type editors, looked
 * up through the field-type descriptor registry. The issue view's field rows
 * and the issues table's custom-field columns both render this — the
 * per-type wiring lives here exactly once. The host owns dataToField/fieldDto
 * conversion: `value` arrives in field space and `onUpdate` fires in field
 * space.
 *
 * Commit routing mirrors what each editor can produce: the generic inline
 * renderers commit through the cell's VALIDATED path (free-typed input can be
 * invalid); overlay and in-place editors (date, select, member, checkbox,
 * photos, paragraph) write through `onUpdate` directly — their values are
 * constrained by the picker and cannot fail validation.
 */

export type CustomFieldValueCellApi = EditableValueCellApi;

export type CustomFieldValueCellProps = Omittable<{
  field: CustomFieldDef | undefined;
  /** Field-space value (already run through dataToField → fieldValue). */
  value: unknown;
  isActive?: boolean;
  /** Cell height in px; the photos grid treats it as a minimum. Omit inside
   *  a table cell — the cell fills the host's ring inset instead. */
  height?: number;
  /** End-edge bleed into the host row's padding (the inline-ROW look).
   *  Table hosts pass false — a table cell clips the overhang square. */
  bleedEnd?: boolean;
  /** Host-tracked highlight: while false, overlays and touch reveal dismiss. */
  highlighted?: boolean;
  /** Fires with field-space values; `undefined` means "remove the value". */
  onUpdate: (value: unknown) => void;
  /** Any overlay (picker, popover, touch reveal) opened or closed. */
  onOverlayChange?: (open: boolean) => void;
  onInvalidStateChange?: (invalid: boolean) => void;
  /** Local validation error entering/leaving — feeds the host's label. */
  onValidationErrorChange?: (message: string | null) => void;
  // Photos-field delta writes (view mode only). Present → the renderer commits
  // each landed upload / removed tile as a single-photo add/remove instead of
  // a full-array overwrite, so concurrent uploads can't clobber each other.
  onAddPhoto?: (photoId: string) => void | Promise<void>;
  onRemovePhoto?: (photoId: string) => void | Promise<void>;
  // Option-field picker extras: the gated inline "Create <name>" row and the
  // field manager's in-picker option editing. Both optional — a host without
  // them gets the plain searchable picker.
  onCreateOption?: OnCreateOption;
  optionActions?: OptionActions;
  onActivate?: () => void;
  onDeactivate?: () => void;
  /** Force-hide the value actions (e.g. while the host's row menu is up). */
  suppressActions?: boolean;
  // When true, the cell renders the display value with no hover, no
  // cursor pointer, no editor activation, and no edit/clear actions.
  // Used for view / comment access tiers.
  readOnly?: boolean;
  /** Prefixes photo download filenames in the lightbox. */
  downloadPrefix?: string;
  /** Host hook for "Reset value"-style chrome. */
  apiRef?: React.RefObject<CustomFieldValueCellApi | null>;
  /**
   * Where the cell sits. `cell` (default) is the table/property-row grammar:
   * the 12px text inset, the outline hover ring, the themed editing ring,
   * the type's full value-action set. `card` is the board card's token: the
   * cell fills a bordered token's value segment: the TOKEN washes on hover
   * and turns its edge brand while active and destructive while invalid
   * (read `isActive` and `onValidationErrorChange`), so the cell paints no
   * hover and no ring of its own, pads 8px, keeps only copy (and map, for an
   * address) at its end edge — the card is a glance surface; open/clear live
   * in the drawer — and reads a clipped value out through the text's
   * overflow tooltip.
   */
  surface?: "cell" | "card";
}>;

export function CustomFieldValueCell({
  field,
  value,
  isActive = false,
  height,
  bleedEnd = true,
  highlighted = false,
  onUpdate,
  onOverlayChange,
  onInvalidStateChange,
  onValidationErrorChange,
  onAddPhoto,
  onRemovePhoto,
  onCreateOption,
  optionActions,
  onActivate,
  onDeactivate,
  suppressActions = false,
  readOnly = false,
  downloadPrefix,
  apiRef,
  surface = "cell",
}: CustomFieldValueCellProps) {
  const isCard = surface === "card";
  // Fullscreen photo viewer (photos fields' "fullscreen" value action).
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Paragraph draft state. The overlay editor renders from these refs;
  // `edited` gates commit so opening + closing a legacy value never dirties
  // it, and `open` mirrors the overlay state for the unmount flush below.
  const paragraphDraftRef = useRef("");
  const paragraphEditedRef = useRef(false);
  const paragraphOpenRef = useRef(false);

  // An open, edited paragraph draft is committed when the cell unmounts
  // (dialog closed, tab switched) — the close handler never got to run.
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;
  const fieldExistsRef = useRef(!!field);
  fieldExistsRef.current = !!field;
  useEffect(
    () => () => {
      if (
        paragraphOpenRef.current &&
        fieldExistsRef.current &&
        paragraphEditedRef.current
      ) {
        onUpdateRef.current(paragraphDraftRef.current);
      }
    },
    [],
  );

  const descriptor = field ? getFieldTypeDescriptor(field.type) : null;

  const displayValue = useMemo(
    () => (field ? getCustomFieldDisplayValue(field, value) : NONE_OPTION_LABEL),
    [field, value],
  );
  const hasValue = useMemo(
    () => (field ? !isCustomFieldValueEmpty(field, value) : false),
    [field, value],
  );
  const flaggedPhone = isFlaggedPhoneValue(field, value, hasValue);

  const valueActionText = useMemo(
    () => (field ? getValueActionText(field.type, value, displayValue) : null),
    [displayValue, field, value],
  );
  const protocolHref = useMemo(
    () =>
      field && hasValue ? getProtocolHref(field.type, valueActionText) : null,
    [field, hasValue, valueActionText],
  );

  const openAddressOnMap = useOpenAddressOnMap("table_cell");

  const validate = useCallback(
    (next: unknown): string | null => {
      if (!field) return null;
      const result = validateCustomFieldValue(field, next);
      return result.valid ? null : (result.message ?? `Invalid ${field.name}`);
    },
    [field],
  );

  // Opens the protocol (mailto / tel / website) for protocol fields. Wired to
  // a dedicated action button — the cell click itself always edits. No overlay
  // opens here, so this must NOT report through onOverlayChange: nothing would
  // ever emit the matching close and the host's highlight would stick.
  const handleOpenProtocol = useCallback(() => {
    if (!field || !protocolHref || typeof window === "undefined") return;
    blurActiveElement();
    if (field.type === "url") {
      window.open(protocolHref, "_blank", "noopener,noreferrer");
    } else {
      window.location.href = protocolHref;
    }
  }, [field, protocolHref]);

  const actions = useMemo<EditableValueCellAction[]>(() => {
    if (!field || !descriptor || !hasValue) return [];
    const noun = descriptor.actionNoun;
    const list: EditableValueCellAction[] = [];
    for (const action of descriptor.actions) {
      switch (action) {
        case "open":
          // Drop the protocol action when the value can't form a valid href.
          if (!protocolHref) break;
          list.push({
            key: "open",
            icon: descriptor.protocolIcon ?? "external-link",
            label: `${descriptor.protocolVerb ?? "Open"} ${noun}`,
            tooltip:
              descriptor.protocolTooltip ?? descriptor.protocolVerb ?? "Open",
            onSelect: handleOpenProtocol,
          });
          break;
        case "map":
          // The address text is what gets located — no text, nothing to find.
          if (!valueActionText) break;
          list.push({
            key: "map",
            icon: "map-pin",
            label: `Open ${noun} in Map`,
            tooltip: "Open in Map",
            onSelect: () => {
              blurActiveElement();
              void openAddressOnMap({ wholeAddress: valueActionText });
            },
          });
          break;
        case "copy":
          list.push(buildCopyValueAction(noun, () => valueActionText));
          break;
        case "fullscreen":
          list.push({
            key: "fullscreen",
            icon: "maximize-2",
            label: `View ${noun} full screen`,
            tooltip: "Full screen",
            onSelect: () => setLightboxOpen(true),
          });
          break;
        case "clear":
          list.push({
            key: "clear",
            icon: "x",
            label: `Clear ${noun}`,
            tooltip: "Clear",
            // Clearing writes the type's empty default as a real value (not
            // the `undefined` remove signal), skipping validation — the
            // default is safe by construction.
            onSelect: (cell) =>
              cell.overwrite(getDefaultValueByType(field.type) ?? null),
          });
          break;
      }
    }
    // Read-only viewers can still copy the value — strip edit / clear
    // actions but keep `copy`. The card keeps the READ actions — copy, the
    // protocol open (call / email / website) and map: what a board reader
    // does with a value — and drops the writes (clear, full screen): its
    // tokens are a glance surface, and destructive next to a drag handle is
    // not card material. The actions collapse at rest and ride in flow on
    // hover, so they cost the value nothing until the pointer arrives.
    if (isCard)
      return list.filter(
        (a) => a.key === "copy" || a.key === "open" || a.key === "map",
      );
    return readOnly ? list.filter((a) => a.key === "copy") : list;
  }, [
    descriptor,
    field,
    handleOpenProtocol,
    hasValue,
    isCard,
    openAddressOnMap,
    protocolHref,
    readOnly,
    valueActionText,
  ]);

  if (!field || !descriptor) {
    return (
      <Box fill="flex" minWidth={0} direction="row" align="stretch">
        <Box fill="flex" justify="center" paddingX="md" paddingY="sm">
          <Text size="sm" variant="muted">
            {"—"}
          </Text>
        </Box>
      </Box>
    );
  }

  const isPhotosField = descriptor.editor === "photos";
  const mediaIds = isPhotosField
    ? (Array.isArray(value) ? value : []).filter(
        (v): v is string => typeof v === "string",
      )
    : [];

  const isOptionField = descriptor.editor === "select";

  // Every display node is inset-free and un-sized: the cell wears the inset
  // (`inset="md"`, below) in both its rest and editing states, and sizes the
  // rest trigger itself — a display that set its own height sat 1px low
  // inside the 1px-bordered trigger.
  const display =
    descriptor.editor === "member" ? (
      // Members render as themselves — a cell is the avatar stack, one member
      // or many, and the hover preview carries the name.
      <Box fill="flex" direction="row" align="center">
        <CustomFieldMemberValue value={value} surface="cell" />
      </Box>
    ) : isOptionField && hasValue ? (
      // Option values are chips at rest — the same chips the picker trigger
      // and every other option surface render, so activating the cell adds
      // the ring and nothing else. Tonal for Single Select, a one-line soft
      // cluster (+N overflow) for Multi Select; the field's showOptionColors
      // decides the hue.
      <Box fill="flex" direction="row" align="center">
        <OptionValueChips
          field={field}
          value={value}
          multiple={field.type === "labels"}
        />
      </Box>
    ) : isPhotosField && readOnly ? (
      <Box fill="flex" direction="row" wrap gap="sm" paddingY="2xs" align="center">
        {mediaIds.map((photoId, index) => (
          <ResolvedMediaTile
            key={`${photoId}-${index}`}
            photoId={photoId}
            size={FIELD_MEDIA_TILE_SIZE}
          />
        ))}
      </Box>
    ) : (
      <Box fill="flex" direction="row" align="center" gap="xs">
        {field?.type === "url" && hasValue && <UrlFavicon url={String(value)} />}
        {/* The flag marker hugs the text in a table cell, where the column
            is wide and the eye reads left to right; on the card the text
            fills the row and the marker pins at the END edge, the row's
            trailing seat. */}
        <Box fill={flaggedPhone && !isCard ? undefined : "flex"} shrink minWidth={0}>
          <Text
            size="sm"
            // The empty marker sits on the faint rung — under the column
            // header's own ink — so an unset cell reads as quiet, not as a
            // value. Same rung the drawer's property rows use.
            variant={
              flaggedPhone
                ? "destructive"
                : displayValue === NONE_OPTION_LABEL
                  ? "faint"
                  : undefined
            }
            truncate={1}
            // The card grammar's clip rule: anything clipped reads out in
            // full on hover. Table and drawer values have a column / the
            // drawer's width to stretch into instead.
            overflowTooltip={isCard}
          >
            {displayValue}
          </Text>
        </Box>
        {flaggedPhone && <InvalidPhoneMarker />}
      </Box>
    );

  const buildEditor = (): EditableValueCellEditor => {
    switch (descriptor.editor) {
      // Checkbox toggles in place — no edit mode. The box always renders,
      // even unset (null), rather than the '—' empty marker. First click
      // writes `true`; further clicks toggle true/false.
      case "checkbox":
        return {
          kind: "content",
          render: () => (
            <InlineValueTrigger
              // The box below only draws the state, so the button carries it.
              aria-checked={value === true}
              aria-label={field.name}
              onClick={() => onUpdate(!(value === true))}
              role="checkbox"
            >
              {/* No inset of its own: the cell wears `inset="md"` around every
                  in-place body, so a second one here would double it. */}
              <Box direction="row" align="center" justify="start">
                {/* Visual-only: the InlineValueTrigger button owns the toggle,
                    so the box only draws the state — `disabled` would dim it. */}
                <Checkbox checked={value === true} nonInteractive />
              </Box>
            </InlineValueTrigger>
          ),
        };

      // Photos edit in place — thumbnails live directly in the cell (no
      // text summary, no activation step); the row grows with the grid.
      case "photos":
        return {
          kind: "content",
          render: () => (
            <>
              <CustomFieldRenderer
                field={field}
                value={value}
                onChangeValue={(next) => onUpdate(next)}
                onAddPhoto={onAddPhoto}
                onRemovePhoto={onRemovePhoto}
              />
              <MediaLightbox
                media={mediaIds.map((id) => ({ id, contentType: "image/*" }))}
                open={lightboxOpen}
                onOpenChange={setLightboxOpen}
                title={field.name}
                downloadPrefix={downloadPrefix}
              />
            </>
          ),
        };

      case "date":
        return {
          kind: "overlay",
          render: ({ open, onOpenChange, cellWithTriggerPart }) => (
            <FieldDatePicker
              field={field}
              value={value}
              onValueChange={(next) => onUpdate(next)}
              open={open}
              onOpenChange={onOpenChange}
              align="start"
            >
              {cellWithTriggerPart(<PopoverTrigger />)}
            </FieldDatePicker>
          ),
        };

      case "select": {
        // THE option picker (searchable rows with swatches, the gated inline
        // "Create <name>" row, manager option editing) — the same surface the
        // issue view rows and the board card open. Values are always the
        // array shape; `labels` is the multi twin BY TYPE.
        const multiple = field.type === "labels";
        const selectValues = Array.isArray(value) ? value.map(String) : [];
        return {
          kind: "overlay",
          render: ({ open, onOpenChange, cellWithTriggerPart }) => (
            <OptionSelector
              field={field}
              values={selectValues}
              multiple={multiple}
              onValuesChange={(vals) => onUpdate(vals)}
              open={open}
              onOpenChange={onOpenChange}
              onCreateOption={onCreateOption}
              optionActions={optionActions}
            >
              {cellWithTriggerPart(<OptionSelectorTrigger />)}
            </OptionSelector>
          ),
        };
      }

      case "member": {
        // Member values are always id arrays; single-mode caps at one element.
        const isSingleMember = !isMultipleField(field.config);
        return {
          kind: "overlay",
          // UserSelector runs the workspace-users pool (a query per
          // instance) and this cell renders once per visible row in a
          // virtualized table — mount it only while its overlay is open. At
          // rest the library cell renders a plain trigger; the member
          // display resolves through the member directory, no pool needed.
          defer: true,
          render: ({ open, onOpenChange, cellWithTriggerPart }) => (
            <UserSelector
              title={field.name}
              value={value as string | string[] | null}
              onValueChange={(ids) =>
                onUpdate(isSingleMember ? (ids[0] ?? null) : ids)
              }
              maxSelections={isSingleMember ? 1 : undefined}
              open={open}
              onOpenChange={onOpenChange}
            >
              {cellWithTriggerPart(<EntitySelectorTrigger />)}
              <EntitySelectorContent size="card" align="start">
                <EntitySelectorInput />
                <EntitySelectorPool value={USER_POOL_ID} />
              </EntitySelectorContent>
            </UserSelector>
          ),
        };
      }

      case "paragraph":
        return {
          kind: "overlay",
          render: (api) => {
            // Mirror the overlay state for the unmount flush: a silent
            // dismissal (host highlight moved) must not flush the draft it
            // discarded.
            paragraphOpenRef.current = api.open;
            const openParagraph = () => {
              // A false return means the touch gate revealed the value
              // actions instead — the draft setup must not run.
              if (!api.onOpenChange(true)) return;
              // Display-only normalize; reset the edited flag so a no-op
              // open/close never persists it.
              paragraphDraftRef.current = normalizeRichTextValue(value);
              paragraphEditedRef.current = false;
              api.deactivate();
            };
            const closeParagraph = () => {
              if (paragraphEditedRef.current) {
                onUpdate(paragraphDraftRef.current);
              }
              blurActiveElement();
              api.onOpenChange(false);
            };
            const handleOpenChange = (next: boolean) =>
              next ? openParagraph() : closeParagraph();
            const editorNode = (
              <RichTextEditor
                label={field.name}
                compact
                mentions
                autoFocus
                content={paragraphDraftRef.current}
                placeholder="Write"
                shortcutHints
                // onUpdate fires only on real user edits (never on content
                // load) — mark dirty.
                onUpdate={(html) => {
                  paragraphDraftRef.current = html;
                  paragraphEditedRef.current = true;
                }}
                contentPaddingX="sm"
                contentPaddingY="sm"
              />
            );
            return (
              // `responsive`: a bottom sheet on a phone, captioned with the
              // field's name; the editor is the sheet's body.
              <Popover open={api.open} onOpenChange={handleOpenChange} responsive>
                {api.cellWithTriggerPart(<PopoverTrigger />)}
                <PopoverPopup
                  align="start"
                  side="bottom"
                  padding="none"
                  sheetTitle={field.name}
                  width="panel"
                >
                  {editorNode}
                </PopoverPopup>
              </Popover>
            );
          },
        };

      // Generic inline editing (text, number, email, phone, url, money,
      // address, and unknown types): the renderer commits through the cell's
      // VALIDATED path and deactivates on blur.
      default:
        return {
          kind: "inline",
          render: ({ commit, deactivate, invalid, reportDraftError }) => (
            <CustomFieldRenderer
              field={field}
              value={value}
              onChangeValue={commit}
              isActive
              onDeactivate={deactivate}
              autoFocus
              invalid={invalid}
              onDraftErrorChange={reportDraftError}
              // The card token is the 28px object floor: 26px inside its
              // hairlines, which is the `sm` control rung on desktop. The
              // row surfaces take each control's own default (30px) inside
              // their 32px cells.
              size={isCard ? "sm" : undefined}
            />
          ),
        };
    }
  };

  return (
    <EditableValueCell
      value={value}
      display={display}
      hasValue={hasValue}
      editor={readOnly ? undefined : buildEditor()}
      triggerLabel={`Edit ${field.name}`}
      height={height}
      inset={isCard ? "sm" : "md"}
      // The card token washes itself and hugs its value: the actions ride
      // in flow after the value, collapsed at rest (`token`).
      restHover={isCard ? "token" : "outline"}
      outline={!isCard}
      grow={isPhotosField}
      bleedEnd={bleedEnd}
      readOnly={readOnly}
      actions={actions}
      suppressActions={suppressActions}
      validate={validate}
      onCommit={onUpdate}
      onInvalidChange={onInvalidStateChange}
      onValidationErrorChange={onValidationErrorChange}
      isActive={isActive}
      onActivate={onActivate}
      onDeactivate={onDeactivate}
      highlighted={highlighted}
      onOverlayChange={onOverlayChange}
      apiRef={apiRef}
    />
  );
}
